import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"

/**
 * Converts a DatoCMS rich-text HTML string into clean plain text for use inside
 * the FAQPage JSON-LD. Google/LLM parsers accept limited inline HTML in
 * acceptedAnswer.text, but plain text is safer and avoids invalid markup.
 */
const toPlainText = html => {
  if (!html) return ""

  return html
    .replace(/<li[^>]*>/gi, " • ")
    .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&rsquo;/gi, "'")
    .replace(/\s+/g, " ")
    .trim()
}

const slugifyId = (str, i) =>
  `faq-${i}-${(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)}`

const FaqItem = ({ question, answer, index, isOpen, onToggle }) => {
  const id = slugifyId(question, index)
  const panelRef = useRef(null)
  const innerRef = useRef(null)
  const hasMounted = useRef(false)

  useEffect(() => {
    const panel = panelRef.current
    const inner = innerRef.current
    if (!panel || !inner) return undefined

    // On first render, snap to the correct state without animating.
    if (!hasMounted.current) {
      hasMounted.current = true
      panel.style.height = isOpen ? "auto" : "0px"
      return undefined
    }

    if (isOpen) {
      // 0px -> measured height. Settle on `auto` once the transition finishes
      // so the panel reflows correctly if the viewport is resized or the
      // content changes while open.
      panel.style.height = `${inner.offsetHeight}px`

      const handleEnd = event => {
        if (event.target !== panel || event.propertyName !== "height") return
        panel.style.height = "auto"
      }

      panel.addEventListener("transitionend", handleEnd)
      return () => panel.removeEventListener("transitionend", handleEnd)
    }

    // Closing: `auto` can't be transitioned from, so pin the current height in
    // pixels, force a reflow to commit it, then collapse on the next frame.
    panel.style.height = `${inner.offsetHeight}px`
    // eslint-disable-next-line no-unused-expressions
    panel.offsetHeight
    panel.style.height = "0px"

    return undefined
  }, [isOpen])

  return (
    <div className="w-full border-b border-grey-medium">
      <h3 className="m-0">
        <button
          type="button"
          id={`${id}-control`}
          aria-expanded={isOpen}
          aria-controls={`${id}-panel`}
          onClick={onToggle}
          className="flex items-start justify-between w-full py-6 text-left transition duration-300 ease-in-out group focus:outline-none md:py-8"
        >
          <span className="block pr-6 text-lg font-bold leading-tight transition duration-300 ease-in-out md:text-xl font-display text-secondary-dark group-hover:text-primary group-focus:text-primary">
            {question}
          </span>

          <span
            aria-hidden="true"
            className={`flex items-center justify-center flex-shrink-0 w-8 h-8 mt-1 transition duration-300 ease-in-out rounded-full ${
              isOpen
                ? "bg-primary text-white"
                : "bg-grey text-secondary-dark group-hover:bg-primary group-hover:text-white group-focus:bg-primary group-focus:text-white"
            }`}
          >
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition duration-300 ease-in-out transform ${
                isOpen ? "-rotate-180" : "rotate-0"
              }`}
            >
              <path
                d="M1 1.5L6 6.5L11 1.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </h3>

      <div
        id={`${id}-panel`}
        ref={panelRef}
        role="region"
        aria-labelledby={`${id}-control`}
        className={`faq-panel ${isOpen ? "is-open" : ""}`}
      >
        <div ref={innerRef} className="faq-panel__inner pb-6 md:pb-8">
          <div
            className="content content--nofirst content--primary-list md:w-11/12"
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        </div>
      </div>
    </div>
  )
}

const Faqs = ({ heading, blurb, items, includeSchema }) => {
  const faqs = (items || []).filter(item => item && item.question && item.answer)
  const [openIndex, setOpenIndex] = useState(null)

  if (!faqs.length) return null

  const schemaFaqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: toPlainText(question),
      acceptedAnswer: {
        "@type": "Answer",
        text: toPlainText(answer),
      },
    })),
  }

  return (
    <section className="py-12 overflow-hidden border-b md:py-20 xl:py-24 border-grey-light">
      {includeSchema && (
        <Helmet defer={false}>
          <script type="application/ld+json">
            {JSON.stringify(schemaFaqPage)}
          </script>
        </Helmet>
      )}

      <div className="container">
        <div className="flex flex-wrap justify-center">
          <div className="w-full max-w-3xl">
            <div className="mb-8 md:mb-12">
              <h2 className="mb-4">{heading || "Frequently asked questions"}</h2>
              {blurb && (
                <div
                  className="content content--nofirst"
                  dangerouslySetInnerHTML={{ __html: blurb }}
                />
              )}
            </div>

            <div className="w-full border-t border-grey-medium">
              {faqs.map((faq, i) => (
                <FaqItem
                  key={i}
                  index={i}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

Faqs.propTypes = {
  heading: PropTypes.string,
  blurb: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
    })
  ),
  includeSchema: PropTypes.bool,
}

Faqs.defaultProps = {
  heading: ``,
  blurb: ``,
  items: [],
  includeSchema: true,
}

export default Faqs
